<template>
  <div
    class="card-wrapper"
    v-if="checkPermission(['chartView_student_status_and_attendance'])"
  >
    <div class="card-top">
      <div class="card-header">
        <div class="card-heading">
          <div class="card-icon">
            <i class="el-icon-user-solid"></i>
          </div>
          <div>
            <p class="card-title">Trạng thái học sinh</p>
            <p class="card-subtitle">Tổng quan phân HS theo trạng thái hiện tại</p>
            <div class="header-tags">
              <span class="header-tag">Tổng {{ totalStudents }} học sinh</span>
              <span class="header-tag header-tag--soft">Nổi bật: {{ leadingStatusLabel }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="summary-row" v-if="loaded && summaryCards.length">
        <div
          v-for="summary in summaryCards"
          :key="summary.label"
          class="summary-card"
          :style="{ '--summary-accent': summary.color }"
        >
          <span class="summary-label">{{ summary.label }}</span>
          <strong class="summary-value">{{ summary.value }}</strong>
          <span class="summary-percent">{{ summary.percent }}%</span>
        </div>
      </div>
    </div>

    <div class="chart-shell">
      <div class="chart-content" v-if="isLoading">
        <div class="state-card state-card--loading">
          <i class="el-icon-loading state-icon"></i>
          <div>
            <p class="state-title">Đang tải dữ liệu biểu đồ</p>
            <span class="state-text">Hệ thống đang cập nhật phân bổ trạng thái học sinh mới nhất.</span>
          </div>
        </div>
      </div>

      <div class="chart-content" v-else-if="loaded && hasData">
        <div class="chart-layout">
          <div class="chartPie">
            <ChartPie
              :chartData="dataConnection.chartData"
              :options="dataConnection.options"
              :checkLable="true"
              :height="300"
            ></ChartPie>
            <div class="donut-center-text">
              <p class="center-value">{{ totalStudents }}</p>
              <p class="center-label">Học sinh</p>
            </div>
          </div>

        </div>
      </div>

      <div class="chart-content chart-empty" v-else>
        <div class="state-card state-card--empty">
          <i class="el-icon-data-analysis state-icon"></i>
          <div>
            <p class="state-title">Chưa có dữ liệu trạng thái học sinh</p>
            <span class="state-text">Khi có dữ liệu, biểu đồ donut và tỷ trọng từng trạng thái sẽ hiển thị tại đây.</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ChartPie from "@/views/chartStatistical/chart/ChartPie.vue";
import checkPermission from "@/utils/permission";
import moment from "moment";
import ChartStatisticalService from "@/services/ChartStatisticalService";

export default {
  components: { ChartPie },
  name: "StatusChart",
  data() {
    return {
      dataConnection: {},
      loaded: false,
      isLoading: false,
      donutCutout: 45,
      dataList: [0, 0, 0, 0],
      statusLabels: ["Đang học", "Chờ học", "Bảo lưu", "Nghỉ học"],
      statusColors: ["#7c4dff", "#4f8dfd", "#ffb547", "#ff6b6b"],
      dataSearch: {
        type: "date",
        dateList: [
          moment().subtract(29, "days").format("YYYY-MM-DD"),
          moment().format("YYYY-MM-DD"),
        ],
      },
    };
  },
  computed: {
    totalStudents() {
      return this.dataList.reduce((sum, item) => sum + Number(item || 0), 0);
    },
    hasData() {
      return this.totalStudents > 0;
    },
    legendItems() {
      return this.statusLabels.map((label, index) => {
        const value = Number(this.dataList[index] || 0);
        const percent = this.totalStudents > 0 ? ((value * 100) / this.totalStudents).toFixed(1) : "0.0";
        return {
          label,
          color: this.statusColors[index],
          value,
          percent,
        };
      });
    },
    summaryCards() {
      return this.legendItems;
    },
    leadingStatusLabel() {
      if (!this.hasData) {
        return "Chưa có dữ liệu";
      }

      const topItem = [...this.legendItems].sort((leftItem, rightItem) => rightItem.value - leftItem.value)[0];
      return topItem && topItem.label ? topItem.label : "Chưa có dữ liệu";
    },
  },
  async mounted() {
    await this.searchByProperties();
  },
  methods: {
    checkPermission,
    fillDataAll() {
      this.dataConnection = {
        chartData: {
          labels: this.statusLabels,
          datasets: [
            {
              backgroundColor: this.statusColors,
              data: this.dataList,
              borderWidth: 6,
              borderColor: "#ffffff",
              hoverBorderWidth: 6,
              hoverBorderColor: "#ffffff",
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutoutPercentage: this.donutCutout,
          layout: {
            padding: {
              top: 12,
              right: 12,
              left: 12,
              bottom: 12,
            },
          },
          legend: {
            display: false,
          },
          tooltips: {
            backgroundColor: "rgba(19, 31, 53, 0.92)",
            titleFontSize: 12,
            bodyFontSize: 12,
            xPadding: 12,
            yPadding: 12,
            callbacks: {
              label: (tooltipItem, data) => {
                const value = Number(data.datasets[0].data[tooltipItem.index] || 0);
                const sum = data.datasets[0].data.reduce((acc, item) => acc + Number(item || 0), 0);
                const percent = sum > 0 ? ((value * 100) / sum).toFixed(1) : "0.0";
                const label = data.labels[tooltipItem.index] || "";
                return `${label}: ${value} (${percent}%)`;
              },
            },
          },
          plugins: {
            datalabels: {
              color: "#ffffff",
              anchor: "center",
              align: "center",
              clamp: true,
              clip: false,
              borderRadius: 12,
              padding: {
                top: 6,
                right: 8,
                bottom: 6,
                left: 8,
              },
              backgroundColor: (ctx) => ctx.dataset.backgroundColor[ctx.dataIndex],
              font: {
                size: 12,
                weight: "700",
              },
              formatter: (value, ctx) => {
                const sum = ctx.dataset.data.reduce((acc, item) => acc + Number(item || 0), 0);
                if (!sum || !value) return "";
                const percent = (value * 100) / sum;
                return percent >= 5 ? `${percent.toFixed(1)}%` : "";
              },
            },
          },
        },
      };
    },
    async searchByProperties() {
      this.isLoading = true;
      try {
        const resp = await ChartStatisticalService.findAllChartKidsStatus();
        this.dataList = Array.isArray(resp.data.data) ? resp.data.data : [0, 0, 0, 0];
        this.fillDataAll();
        this.loaded = true;
      } catch (e) {
        this.$message({
          message: "Failed to get the data",
          type: "error",
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.card-top {
  display: flex;
  flex-direction: column;
}

.card-wrapper {
  background: linear-gradient(180deg, #ffffff 0%, #f9fbff 100%);
  border-radius: 24px;
  box-shadow: 0 18px 40px rgba(31, 45, 61, 0.08);
  padding: 20px;
  margin: 20px 0;
  transition: all 0.3s ease;
  height: 730px;
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(122, 147, 184, 0.12);
}

.card-wrapper:hover {
  box-shadow: 0 22px 48px rgba(31, 45, 61, 0.12);
  transform: translateY(-4px);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 18px;
}

.card-heading {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.card-icon {
  width: 52px;
  height: 52px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: #ffffff;
  background: linear-gradient(135deg, #7c4dff, #4f8dfd);
  box-shadow: 0 14px 24px rgba(79, 141, 253, 0.24);
}

.card-title {
  margin: 0;
  font-size: 20px;
  font-weight: 800;
  color: #22324d;
}

.card-subtitle {
  margin: 6px 0 0;
  font-size: 13px;
  color: #7f8da3;
}

.header-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.header-tag {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  color: #31527f;
  background: rgba(79, 141, 253, 0.12);
}

.header-tag--soft {
  background: rgba(124, 77, 255, 0.12);
  color: #654ca5;
}

.summary-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 18px;
}

.summary-card {
  position: relative;
  padding: 16px 18px;
  border-radius: 18px;
  background: #ffffff;
  border: 1px solid rgba(118, 153, 214, 0.14);
  box-shadow: 0 10px 24px rgba(31, 45, 61, 0.06);
  overflow: hidden;
}

.summary-card::before {
  content: "";
  position: absolute;
  inset: 0 auto 0 0;
  width: 4px;
  background: var(--summary-accent);
}

.summary-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #7d8ca1;
  margin-bottom: 8px;
}

.summary-value {
  display: block;
  font-size: 24px;
  line-height: 1;
  color: #21314c;
}

.summary-percent {
  display: inline-block;
  margin-top: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #71829b;
}

.chart-shell {
  position: relative;
  flex: 1;
  height: 390px;
  padding: 18px;
  border-radius: 24px;
  background:
    radial-gradient(circle at top right, rgba(124, 77, 255, 0.12), transparent 32%),
    linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  border: 1px solid rgba(118, 153, 214, 0.14);
}

.chart-content {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.chart-caption {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  margin-bottom: 14px;
  border-radius: 999px;
  background: rgba(79, 141, 253, 0.08);
  color: #4f6483;
  font-size: 13px;
  font-weight: 600;
}

.chart-caption__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #7c4dff;
  box-shadow: 0 0 0 4px rgba(124, 77, 255, 0.14);
}

.chart-layout {
  display: flex;
  flex-direction: column;
  gap: 20px;
  height: 100%;
}

.chartPie {
  width: 100%;
  max-width: 360px;
  margin: 0 auto;
  position: relative;
}

.donut-center-text {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  pointer-events: none;
}

.center-value {
  margin: 0;
  font-size: 30px;
  line-height: 1;
  font-weight: 800;
  color: #26344c;
}

.center-label {
  margin: 6px 0 0;
  font-size: 11px;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #8b99ad;
}

.status-legend {
  margin-top: 2px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(118, 153, 214, 0.12);
}

.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
  box-shadow: 0 0 0 4px rgba(79, 141, 253, 0.08);
}

.legend-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.legend-label {
  font-size: 13px;
  font-weight: 700;
  color: #33425d;
}

.legend-sub {
  margin-top: 2px;
  font-size: 11px;
  color: #8695a9;
}

.legend-value {
  font-size: 16px;
  font-weight: 800;
  color: #24344d;
}

.state-card {
  min-height: 240px;
  border-radius: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 24px;
  text-align: left;
}

.state-card--loading {
  background: linear-gradient(135deg, rgba(79, 141, 253, 0.08), rgba(124, 77, 255, 0.08));
}

.state-card--empty {
  background: linear-gradient(135deg, rgba(242, 247, 255, 0.9), rgba(248, 251, 255, 0.95));
}

.state-icon {
  font-size: 36px;
  color: #4f8dfd;
}

.state-title {
  margin: 0 0 6px;
  font-size: 17px;
  font-weight: 700;
  color: #26344c;
}

.state-text {
  font-size: 13px;
  color: #7d8ca1;
  line-height: 1.6;
}

@media (max-width: 1200px) {
  .card-wrapper {
    min-height: auto;
  }

  .summary-row {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .chart-shell {
    padding: 14px;
  }

  .state-card {
    flex-direction: column;
    text-align: center;
  }
}
</style>
